import React, { useMemo } from 'react';
import collect from 'collect.js';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Empty, Spin } from 'antd';
import useStatistics from '../../../../hooks/data/useStatistics';
import StatisticsProvider from '../../StatisticsProvider';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

function BarChart() {
  const { stats, loading, getMetricLabel } = useStatistics();

  const labels = collect(stats)
    .keys()
    .map((key) => getMetricLabel(key))
    .toArray();
  const values = collect(stats).pluck('value').toArray();

  const chartData = {
    labels,
    datasets: [
      {
        labels,
        data: values,
        pointRadius: 1,
        pointHoverRadius: 2,
        fill: 'start',
        tension: 0,
        backgroundColor: 'rgba(105, 206, 140, 1)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => tooltipItem.raw,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          ticks: {
            callback: (value) => getMetricLabel(value),
          },
        },
      },
      y: {
        title: {
          display: false,
        },
        ticks: {
          stepSize: 1,
          callback: (value) => value,
        },
      },
    },
  };

  const renderLoading = useMemo(
    () =>
      loading && (
        <div className="absolute inset-0 flex justify-center items-center">
          <Spin size="large" />
        </div>
      ),
    [loading],
  );

  const renderEmpty = useMemo(() => {
    const hasNoData = values.length === 0 || values.every((value) => value === 0 || value === null);

    return hasNoData && !loading ? (
      <div className="absolute inset-0 flex justify-center items-center">
        <Empty />
      </div>
    ) : null;
  }, [stats, loading]);

  return (
    <div className="relative">
      <Bar data={chartData} options={chartOptions} />
      {renderEmpty}
      {renderLoading}
    </div>
  );
}

export default function Orders({ dateRangeFilter }) {
  return (
    <div className="mt-md">
      <StatisticsProvider
        mainMetrics={[
          'google_seo_orders_count',
          'google_business_orders_count',
          'google_ads_orders_count',
          'flyer_orders_count',
          'email_orders_count',
          'other_orders_count',
        ]}
        dateRangeFilter={dateRangeFilter}
      >
        <BarChart />
      </StatisticsProvider>
    </div>
  );
}
